<i18n>
{
	"en": {
		"etd": "Departure",
		"editTrip": "Edit trip",
		"from": "From",
		"status": "Status",
		"to": "To",
		"tripNumber": "Trip",
		"type": "Type",
		"noTripsFound": "No trips were found that match this search. Try expanding your search criteria.",
		"tripsFound": "No trips found | {count} trip found | {count} trips found",
		"vendorName": "Vendor",
		"vendorNumber": "Vendor No.",
		"orders": "Orders",
		"loadingList": "Loading List",
		"loadingListLandscape": "Landscaped Loading List",
		"linehaul": "Linehaul",
		"external": "External",
		"distribution": "Distribution",
		"loadingMeters": "Loading",
		"volume": "Volume",
		"grossWeight": "Gr. Weight",
		"sendTripOnEdi" : "Send on EDI",
		"tripSentOnEdi" : "Trip sent on EDI",
		"tripAlreadyQueuedForSending" : "Trip has already been sent on EDI!",
		"wasSent": "Sent",
		"yes": "Yes",
		"no": "No",
		"newTrip": "New trip",
		"deleteTrips": "Delete Trips | Delete selected trip | Delete selected ({count}) trips",
		"edi": "EDI",
		"status/cancelled": "Cancelled",
		"status/draft": "Draft",
		"status/ready": "Ready",
		"status/planned": "Planned",
		"status/rejected": "Rejected",
		"status/unsuccessful": "Unsuccessful",
		"status/requested": "Requested",
		"status/received": "Received",
		"status/accepted": "Accepted",
		"status/readyForPickup": "Ready for Pickup",
		"status/loaded": "Loaded",
		"status/unloaded": "Unloaded"
	},
	"no": {
		"etd": "Avgang",
		"editTrip": "Rediger tur",
		"from": "Fra",
		"status": "Status",
		"to": "Til",
		"tripNumber": "Tur",
		"type": "Type",
		"noTripsFound": "Vi fant ingen turer som matcher dette søket. Prøv å utvide søkekriteriene.",
		"tripsFound": "Ingen turer | {count} tilgjengelig tur | {count} turer",
		"vendorName": "Vendor",
		"vendorNumber": "Vendor Nr.",
		"orders": "Oppdrag",
		"loadingList": "Lasteliste",
		"loadingListLandscape": "Lasteliste landskap",
		"linehaul": "Langtransport",
		"external": "Ekstern",
		"distribution": "Distribusjon",
		"loadingMeters": "Lastm.",
		"volume": "Volum",
		"grossWeight": "Br. vekt",
		"sendTripOnEdi" : "Send på EDI",
		"tripSentOnEdi" : "Turen er sent på EDI",
		"tripAlreadyQueuedForSending" : "Turen er allerede sendt på EDI!",
		"wasSent": "Sendt",
		"yes": "Ja",
		"no": "Nei",
		"newTrip": "Ny tur",
		"deleteTrips": "Slett turer | Slett valgt tur | Slett valgte ({count}) turer ",
		"edi": "EDI",
		"status/cancelled": "Annullert",
		"status/draft": "Kladd",
		"status/ready": "Klar",
		"status/planned": "Planlagt",
		"status/rejected": "Avslått",
		"status/unsuccessful": "Bomtur",
		"status/requested": "Forespurt",
		"status/received": "Mottatt",
		"status/accepted": "Akseptert",
		"status/readyForPickup": "Klar for henting",
		"status/loaded": "Lastet",
		"status/unloaded": "Losset"
	}
}
</i18n>

<template>
	<t-vertical-panel :hideSubHeader="!tripsFound" class="trip-list">
		<div slot="header" class="trip-list-header">
			<t-panel-header icon="trip">
				{{ $tc('tripsFound', tripsFound) }}
			</t-panel-header>

			<div class="trip-list-summary">
				<t-metric-details
					:value="totalVolume"
					decimals="1"
					hide-pie-icon
					unit="m3"
				/>
				<t-metric-details
					:value="totalWeight"
					hide-pie-icon
					unit="kg"
				/>
				<t-metric-details
					:value="totalLoadingMeters"
					decimals="1"
					hide-pie-icon
					unit="lm"
				/>
			</div>
		</div>

		<div slot="sub-header" class="trip-list-sub-header">
			<div  class="button-group-left">
				<t-button
					:value="$t('newTrip')"
					appearance="minimal"
					type="bp"
					iconBefore="plus"
					@click="openNewTripModal"
				/>
				<t-icon-button
					:disabled="!rowsSelected"
					appearance="minimal"
					type="bp"
					icon="trash"
					intent="danger"
					:value="$tc('deleteTrips', rowsSelected)"
					@click="deleteSelectedTrips"
				/>
				<t-button
					:disabled="!transmittableToEdi"
					appearance="minimal"
					width="150px"
					type="bp"
					iconBefore="edi"
					@click="onScheduleTripOnEdi"
				>
					{{ $t('sendTripOnEdi') }}
				</t-button>
			</div>

			<div class="button-group-right">
				<t-button
					:value="$t('loadingList')"
					:disabled="!singleTripSelected"
					appearance="minimal"
					type="timpex"
					iconBefore="waybill"
					@click="showLoadingList"
				/>
				<t-button
					:value="$t('loadingListLandscape')"
					:disabled="!singleTripSelected"
					appearance="minimal"
					type="bp"
					iconBefore="label"
					@click="showLoadingListLandscape"
				/>
				<t-button
					:value="$t('editTrip')"
					:disabled="!editableTrip"
					appearance="minimal"
					width="120px"
					type="bp"
					iconBefore="annotation"
					@click="onEditTrip()"
				/>
			</div>
		</div>

		<div class="trip-list-content">
			<!-- Key disables grid component reuse. Fixes localStorageId changing between views. -->
			<t-grid
				v-if="tripsFound"
				ref="grid"
				:key="localStorageId"
				:columnDefinitions="columnDefinitions"
				:defaultColumnDefinition="defaultColumnDefinition"
				:rowHeight="50"
				:items="trips"
				:localStorageId="localStorageId"
				selectable
				@selected="onRowsSelected"
				@rowDoubleClick="onEditTrip"
				@enter="onEditTrip"
			>
				<template v-slot:tripNumber="{ item }">
					<div v-if="item.type.internalCode === 'external'">
						{{ item.vendor.name }}
					</div>
					<div v-else>
						{{ item.tripNumber }}
					</div>
				</template>
				<template v-slot:status="{ item }">
					<div v-if="item.type.internalCode === 'external'" style="text-align:center">
						<t-timpex-icon
							icon="orders"
							size="md"
							style="padding-left: 3px;"
						/>
					</div>
					<div v-else style="text-align:center">
						<div v-if="item.cargoCarrier">
							<t-timpex-icon
								:icon="getStatusIcon(item)"
								:color="getStatusColor(item)"
								:title="getStatusTitle(item)"
								size="md"
							/>
						</div>
						<div v-if="item.cargoCarrier">
							{{ item.cargoCarrier.registrationNumber }}
						</div>
					</div>
				</template>
				<template v-slot:type="{ item }">
					<t-timpex-icon
						:icon="getTypeIcon(item)"
						size="sm"
						type="bp"
						color="blue"
						style="padding-left: 3px;"
					/>
				</template>
				<template v-slot:fromAddress="{ item }">
					<div v-if="item.type.internalCode === 'external'"/>
					<div v-else>
						<div style="height: 16px;">{{ item.fromAddress.name }}</div>
						<div class="grid-cell-sub-line">{{ item.fromAddress.street }}, {{ item.fromAddress.postalCode }} {{ item.fromAddress.postalPlace }}</div>
					</div>
				</template>
				<template v-slot:toAddress="{ item }">
					<div v-if="item.type.internalCode === 'external'"/>
					<div v-else>
						<div style="height: 16px;">{{ item.toAddress.name }}</div>
						<div class="grid-cell-sub-line">{{ item.toAddress.street }}, {{ item.toAddress.postalCode }} {{ item.toAddress.postalPlace }}</div>
					</div>
				</template>
				<template v-slot:orders="{ value }" class="">
					<div class="grid-cell-center">
						<t-timpex-icon
							icon="order"
							size="sm"
							style="padding-bottom: 3px;"
						/>
					</div>
					<div class="grid-cell-bold grid-cell-center">{{ value }}</div>
				</template>

				<template v-slot:volume="{ item }" class="">
					<div v-if="item.type.internalCode !== 'external'">
						<t-metric-details
							:value="item.volume"
							:max="getMaxVolume(item)"
							decimals="1"
							vertical
							center
						/>
					</div>
				</template>
				<template v-slot:grossWeight="{ item }" class="">
					<div v-if="item.type.internalCode !== 'external'">
						<t-metric-details
							:value="item.grossWeight"
							:max="getMaxWeight(item)"
							vertical
							center
						/>
					</div>
				</template>
				<template v-slot:loadingMeters="{ item }" class="">
					<div v-if="item.type.internalCode !== 'external'">
						<t-metric-details
							:value="item.loadingMeters"
							:max="getMaxLoadingMeters(item)"
							decimals="1"
							vertical
							center
						/>
					</div>
				</template>
			</t-grid>
			<t-no-content-panel
				v-else
				:text="$t('noTripsFound')"
				icon="truck-front"
			/>
			<t-overlay
				:isShown="tripsLoading"
				loaderMarginBottom="50px"
				absolute
				loader
			/>
		</div>
	</t-vertical-panel>
</template>

<script>
import { mapDynamicState, mapDynamicActions } from '@/core/mapDynamic';
import {
	eventBus, displayDateFormat, storageDateFormat, displayDateFormatter, dateExists, statuses, tripTypeIcon, cargoCarrierIcon, sumValues,
} from '@/core';
import moment from 'moment';
import TMetricDetails from '@/components/evergreen/timpex/layers/TMetricDetails';

export default {
	name: 'TripList',
	components: { TMetricDetails },
	data() {
		return {
			defaultColumnDefinition: {
				resizable: true,
				headerFilterInputWithButton: true,
			},
			rowsSelected: 0
		};
	},
	computed: {
		...mapDynamicState('computedStoreNamespace', [ 'trips', 'tripsLoading', 'tripDetails', 'tripsSentOnEdi' ]),
		searchId() {
			return this.$route.params.searchId || -1;
		},
		computedStoreNamespace() {
			return `tripSearch/${this.searchId}`;
		},
		localStorageId() {
			return `tripSearchList/${this.searchId}`;
		},
		tripsFound() {
			return this.trips.length;
		},
		singleTripSelected() {
			return this.rowsSelected === 1;
		},
		transmittableToEdi() {
			return this.singleTripSelected && this.tripDetails && !(this.tripDetails.ediSendingDate || this.tripsSentOnEdi.includes(this.tripDetails.id)) && this.tripDetails.vendor.edipartId;
		},
		editableTrip() {
			return this.singleTripSelected && this.tripDetails && this.tripDetails.type.internalCode !== 'external';
		},
		totalVolume() {
			return sumValues(this.trips, 'volume');
		},
		totalWeight() {
			return sumValues(this.trips, 'grossWeight');
		},
		totalLoadingMeters() {
			return sumValues(this.trips, 'loadingMeters');
		},
		columnDefinitions() {
			return [
				{
					field: 'tripNumber',
					displayName: this.$t('tripNumber'),
					width: 75,
					type: 'number',
					comparableValueGetter: (item) => Number(item.tripNumber),
					sortDirection: 'desc'
				},
				{
					field: 'status',
					displayName: this.$t('status'),
					valueGetter: (item) => item.name,
					resizable: false,
					width: 70,
				},
				{
					field: 'type',
					displayName: this.$t('type'),
					comparableValueGetter: (item) => item.type.id,
					resizable: false,
					width: 53,
					align: 'center',
				},
				{
					field: 'etd.date',
					displayName: this.$t('etd'),
					valueGetter: (item) => displayDateFormatter(this.dateGetter(item.atd, item.etd)),
					width: 90,
					resizable: false,
					type: 'date',
					dateFormat: displayDateFormat,
					comparableValueGetter: (item) => moment(this.dateGetter(item.atd, item.etd), storageDateFormat).startOf('day').toISOString(),
					cellClass(item) { return dateExists(item.atd) ? 'grid-cell-bold' : 'grid-cell-light'; }
				},
				{
					field: 'ediSendingDate',
					displayName: this.$t('edi'),
					valueGetter: (item) => item.ediSendingDate ? displayDateFormatter(item.ediSendingDate.date) : '',
					width: 90,
					resizable: false,
					type: 'date',
					dateFormat: displayDateFormat,
					comparableValueGetter: (item) => moment(item.ediSendingDate.date, storageDateFormat).startOf('day').toISOString(),
					cellClass(item) { return dateExists(item.ediSendingDate) ? undefined : 'grid-cell-light'; }
				},
				{
					field: 'fromAddress',
					displayName: this.$t('from'),
					comparableValueGetter: (item) => {
						if (item.type.internalCode === 'external') return `Via ${item.vendor.name}`;
						return this.addressGetter(item.fromAddress);
					},
					type: 'address',
					minWidth: 125,
				},
				{
					field: 'toAddress',
					displayName: this.$t('to'),
					comparableValueGetter: (item) => {
						if (item.type.internalCode === 'external') return `Via ${item.vendor.name}`;
						return this.addressGetter(item.toAddress);
					},
					type: 'address',
					minWidth: 125,
				},
				{
					field: 'vendor.name',
					displayName: this.$t('vendorName'),
					valueGetter: (item) => item.vendor ? item.vendor.name : ''
				},
				{
					field: 'vendor.vendorNumber',
					displayName: this.$t('vendorNumber'),
					valueGetter: (item) => item.vendor ? item.vendor.vendorNumber : ''
				},
				{
					field: 'orders',
					displayName: this.$t('orders'),
					valueGetter: (item) => item.orders ? item.orders.length : 0,
					align: 'left',
					type: 'number',
					width: 50
				},
				{
					field: 'volume',
					displayName: this.$t('volume'),
					align: 'left',
					type: 'number',
					width: 80
				},
				{
					field: 'grossWeight',
					displayName: this.$t('grossWeight'),
					align: 'left',
					type: 'number',
					width: 80
				},
				{
					field: 'loadingMeters',
					displayName: this.$t('loadingMeters'),
					align: 'left',
					type: 'number',
					width: 80
				},
				{
					field: 'wasSent',
					displayName: this.$t('wasSent'),
					valueGetter: item => item.wasSent ? this.$t('yes') : this.$t('no'),
				},
			];
		}
	},
	methods: {
		...mapDynamicActions('computedStoreNamespace', [
			'setTripDetails',
			'getLoadingList',
			'scheduleTripOnEdi',
			'deleteTrips',
		]),
		async onScheduleTripOnEdi() {
			let [selectedTrip] = this.$refs.grid.getSelectedItems();
			try {
				await this.scheduleTripOnEdi(selectedTrip.id);
				this.$notify({
					type: 'success',
					title: this.$t('tripSentOnEdi')
				});
			} catch (err) {
				this.$notify({
					type: 'error',
					title: this.$t(err.message)
				});
			}
		},
		showLoadingList() {
			let items = this.$refs.grid.getSelectedItems();
			let ids = items.map(x => x.id);
			this.getLoadingList({ tripIds: ids });
		},
		showLoadingListLandscape() {
			let items = this.$refs.grid.getSelectedItems();
			let ids = items.map(x => x.id);
			this.getLoadingList({ tripIds: ids, landscape: true });
		},
		openNewTripModal() {
			eventBus.$emit('toggleNewTripModal',true);
		},
		onEditTrip(id) {
			let trip = undefined;
			if (id) {
				trip = this.trips.find(x => x.id === id);
			} else {
				let selectedItems = this.$refs.grid.getSelectedItems();
				if (selectedItems.length > 1) return;
				trip = selectedItems[0];
			}
			if (!trip) return;
			// TODO: Separate editing for external trips
			if (trip.type.internalCode === 'external') return;
			eventBus.$emit('setEditTrip',trip.id);
			eventBus.$emit('toggleNewTripModal',true);
		},
		addressGetter(party) {
			if (typeof party === 'string') return party;
			if (!party) return '';
			return `${party.name} ${party.street} ${party.postalCode} ${party.postalPlace}`;
		},
		dateGetter(actual, estimated) {
			if (dateExists(actual)) return actual.date;
			if (dateExists(estimated)) return estimated.date;
			return undefined;
		},
		onRowsSelected(ids) {
			this.rowsSelected = ids.length;
			let selectedItems = this.$refs.grid.getSelectedItems();
			if (selectedItems.length > 1) return;

			let [trip] = selectedItems;

			if (trip)
				this.setTripDetails(trip.id);
		},
		getStatusIcon(item) {
			if (!item.cargoCarrier) return 'truck';
			return cargoCarrierIcon.getIcon(item.cargoCarrier.iconId);
		},
		getStatusColor(item) {
			return statuses.color(item.status);
		},
		getStatusTitle(item) {
			return this.$t(`status/${statuses.operational(item.status)}`);
		},
		getTypeIcon(item) {
			return tripTypeIcon(item.type.internalCode);
		},
		getMaxLoadingMeters(item) {
			if (item.cargoCarrier && item.cargoCarrier.loadingMeters)
				return item.cargoCarrier.loadingMeters;
			return undefined;
		},
		getMaxWeight(item) {
			if (item.cargoCarrier && item.cargoCarrier.weight)
				return item.cargoCarrier.weight;
			return undefined;
		},
		getMaxVolume(item) {
			if (item.cargoCarrier && item.cargoCarrier.volume)
				return item.cargoCarrier.volume;
			return undefined;
		},
		deleteSelectedTrips() {
			console.log('delete trips');
			let selectedTripIds = this.$refs.grid.getSelectedItems().map(
				trip => trip.id
			);
			console.log(selectedTripIds);
			this.deleteTrips(selectedTripIds);
		}
	},
};
</script>

<style lang="scss" scoped>
.trip-list {
	.trip-list-header {
		height: 100%;
		display:flex;
		align-items: center;
		padding-left: 15px;
	}
	.button-group-left{
		display: flex;
		height: 100%;
		align-items: center;
		justify-content: flex-end;
		> button:nth-child(3) {
			margin-left: 5px;
		}
	}
	.button-group-right {
		display: flex;
		height: 100%;
		align-items: center;
		justify-content: flex-end;
		> button {
			margin-left: 20px;
		}
	}
	.trip-list-summary {
		display:grid;
		padding: 15px;
		grid-template-columns: repeat(3, max-content);
		grid-column-gap: 20px;
		margin-left: auto;
	}
	.trip-list-sub-header {
		display:flex;
		padding: 0px 15px;
		height: 100%;
		align-items: center;
		justify-content: space-between;
	}
	.trip-list-content {
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 0px 5px;
	}
}
</style>
